import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    MapPin, Star, ArrowRight, ShieldCheck, Calendar, Users, Search,
    Building2, Award, Headphones, Heart,
    BedDouble, BadgeCheck, MessageCircle, Map as MapIcon,
    Tag, Image as ImageIcon, Bus as BusIcon, GraduationCap,
} from "lucide-react";
import PageTransition from "../components/PageTransition";
import FAQSection from "../components/FAQSection";
import SocialStrip from "../components/SocialStrip";
import PrayerTimes from "../components/PrayerTimes";
import DateRangePicker from "../components/DateRangePicker";
import GuestSelector from "../components/GuestSelector";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { resolveImageUrl } from "../lib/imageUrl";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { todayISO, addDaysISO, nightsBetween } from "../lib/dates";
import { stayTotal } from "../lib/roomPricing";
import { toast } from "sonner";
import { resolveAmenities } from "../lib/amenities";

const IMG_MECCA = "https://images.unsplash.com/photo-1542521148-51306e7ffc1e?fm=jpg&q=85&w=1400&auto=format&fit=crop";
const IMG_MEDINA = "https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?fm=jpg&q=85&w=1400&auto=format&fit=crop";
const HERO_BG = "https://images.unsplash.com/photo-1572251057603-b1e3014c5e67?fm=jpg&q=85&w=2400&auto=format&fit=crop";
const HERO_BG_MOBILE = "https://images.unsplash.com/photo-1572251057603-b1e3014c5e67?fm=jpg&q=85&w=900&h=1400&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.6";

const RATING_LABEL = (avg) => {
    if (!avg || avg < 1) return "Yeni";
    if (avg >= 9) return "Olağanüstü";
    if (avg >= 8) return "Çok İyi";
    if (avg >= 7) return "İyi";
    if (avg >= 6) return "Memnun Edici";
    if (avg >= 4) return "Orta";
    return "Zayıf";
};

const SORT_OPTIONS = [
    { id: "popular", label: "Popüler" },
    { id: "price_asc", label: "Fiyat: Düşük → Yüksek" },
    { id: "price_desc", label: "Fiyat: Yüksek → Düşük" },
    { id: "rating", label: "En Yüksek Puan" },
    { id: "distance", label: "Harem'e En Yakın" },
];

const fmtDay = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
};

const HotelCard = ({ h, onQuickBook, formatPrice, recent, t, priceInfo, checkIn, checkOut }) => {
    const { isAuthed, isFavorite, toggleFavorite } = useCustomerAuth();
    const navigate = useNavigate();
    const { nightly: lowest = 0, total: stayPrice = 0, baseNightly = 0, nights = 1 } = priceInfo || {};
    const en = t("home.new") === "New";
    const pct = Number(h.discount_percent) || 0;
    const strike = baseNightly > lowest + 0.01 ? baseNightly : (pct > 0 && lowest ? Math.round(lowest / (1 - pct / 100)) : 0);
    const dateDrop = baseNightly > lowest + 0.01;
    const strikePct = strike > 0 && lowest ? Math.round((1 - lowest / strike) * 100) : 0;
    const stars = h.star_rating || 5;
    const reviewAvg = h.review_average || 0;
    const reviewCount = h.review_count || 0;
    const ratingLabel = RATING_LABEL(reviewAvg);
    const amenities = resolveAmenities(h.amenities || []).slice(0, 6);
    const faved = isFavorite(h.id);

    const handleFav = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isAuthed) {
            toast.info("Favorilere eklemek için üye olun");
            navigate("/login");
            return;
        }
        const res = await toggleFavorite(h.id);
        if (res.ok) toast.success(res.added ? "Favorilere eklendi" : "Favorilerden çıkarıldı");
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.45 }}
            whileHover={{ y: -4 }}
            className="group border border-border_green bg-emerald_surface hover:border-gold transition-colors duration-300 flex flex-col md:flex-row overflow-hidden"
            data-testid={`hotel-card-${h.id}`}
        >
            <div className="relative md:w-80 md:flex-shrink-0 h-60 md:h-auto overflow-hidden block">
                {/* Mobile: swipeable carousel. Desktop: single hero image, click → details. */}
                <div className="h-full">
                    <div
                        className="flex h-full overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar"
                        data-testid={`hotel-card-carousel-${h.id}`}
                    >
                        {(h.images?.length ? h.images : [null]).map((img, i) => (
                            <Link
                                key={img ?? `ph-${i}`}
                                to={`/hotels/${h.id}`}
                                className="snap-center flex-shrink-0 w-full h-full block relative"
                                aria-label={`${h.name} fotoğraf ${i + 1}`}
                            >
                                {img ? (
                                    <img
                                        src={resolveImageUrl(img)}
                                        alt={`${h.name} ${i + 1}`}
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full bg-emerald_deep" />
                                )}
                            </Link>
                        ))}
                    </div>
                    {h.images?.length > 1 && (
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-emerald_deep/70 backdrop-blur px-2 py-1 text-[10px] text-bone/90 pointer-events-none">
                            <ImageIcon className="w-3 h-3 text-gold" /> {h.images.length} {t("home.new") === "New" ? "photos" : "fotoğraf"} · {t("home.new") === "New" ? "swipe" : "kaydır"}
                        </div>
                    )}
                </div>
                <div className="absolute top-3 left-3 bg-emerald_deep/85 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/40 pointer-events-none z-10">
                    {h.city === "Mecca" ? "Mekke" : "Medine"}
                </div>
                <div className="absolute top-3 right-3 flex items-center gap-0.5 bg-emerald_deep/85 backdrop-blur px-2 py-1 pointer-events-none z-10">
                    {Array.from({ length: stars }).map((_, i) => (
                        <Star key={i} className="w-2.5 h-2.5 fill-gold text-gold" />
                    ))}
                </div>
                {h.distance_grade && (
                    <div className="absolute top-12 left-3 bg-rose-600 text-white font-bold text-xs px-2 py-1 shadow-lg pointer-events-none z-10" title="Harem'e ulaşım kolaylığı" data-testid={`grade-${h.id}`}>
                        {h.distance_grade}
                    </div>
                )}
                <button type="button" className={`absolute bottom-3 right-3 w-9 h-9 rounded-full backdrop-blur flex items-center justify-center transition-colors z-10 ${faved ? "bg-rose-500 hover:bg-rose-600" : "bg-emerald_deep/85 hover:bg-gold"}`} aria-label="Favorilere ekle" onClick={handleFav} data-testid={`favorite-${h.id}`}>
                    <Heart className={`w-4 h-4 ${faved ? "fill-white text-white" : "text-bone hover:text-emerald_deep"}`} />
                </button>
            </div>

            <div className="flex-1 flex flex-col p-5 lg:p-6">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <Link to={`/hotels/${h.id}`}>
                            <h3 className="font-serif text-3xl lg:text-[34px] group-hover:text-gold transition-colors leading-tight">
                                {h.name}
                            </h3>
                        </Link>
                        <div className="flex items-center gap-1.5 text-sm text-muted_green mt-2">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">Harem'e {h.distance_to_haram}</span>
                        </div>
                    </div>
                    {reviewCount > 0 ? (
                        <div className="flex items-center gap-2 flex-shrink-0" data-testid={`rating-block-${h.id}`}>
                            <div className="text-right">
                                <div className="text-[11px] uppercase tracking-[0.15em] text-bone/80 font-semibold">{ratingLabel}</div>
                                <div className="text-[10px] text-muted_green">{reviewCount} yorum · /10</div>
                            </div>
                            <div className="bg-gold text-emerald_deep font-bold text-base px-2.5 py-1 rounded-sm min-w-[2.8rem] text-center">
                                {reviewAvg.toFixed(1)}
                            </div>
                        </div>
                    ) : (
                        <div className="badge-new text-[10px] uppercase tracking-[0.2em] text-gold/80 border border-gold/40 px-2 py-1 flex-shrink-0">Yeni</div>
                    )}
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="badge-verified inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-300 bg-emerald-900/30 border border-emerald-700/40 px-2 py-0.5">
                        <BadgeCheck className="w-3 h-3" /> {t("trust.verified_short")}
                    </span>
                    <span className="badge-whatsapp inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-gold/90 border border-gold/40 px-2 py-0.5">
                        <Headphones className="w-3 h-3" /> {t("trust.live_support")}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-300 bg-emerald-900/30 border border-emerald-700/40 px-2 py-0.5">
                        <Tag className="w-3 h-3" /> {t("trust.free_cancel")}
                    </span>
                    {(() => {
                        const totalRooms = (h.rooms || []).reduce((s, r) => s + (Number(r.inventory) || 0), 0);
                        if (totalRooms > 0 && totalRooms <= 5) {
                            return (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-rose-100 bg-rose-700/70 border border-rose-500/60 px-2 py-0.5 font-semibold" data-testid={`urgency-${h.id}`}>
                                    {(t("trust.rooms_left") || "Son {n} oda!").replace("{n}", totalRooms)}
                                </span>
                            );
                        }
                        return null;
                    })()}
                    {(() => {
                        const seed = (h.id || "").charCodeAt(0) + (h.id || "").length;
                        const viewers = 2 + ((seed * 7 + Date.now() / 6e5) | 0) % 9;
                        return (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-amber-200/90 border border-amber-500/40 bg-amber-900/20 px-2 py-0.5">
                                <Users className="w-3 h-3" /> {viewers} {t("trust.viewers_now")}
                            </span>
                        );
                    })()}
                </div>

                {recent && (
                    <div className="mt-2 flex items-center gap-1.5 text-[10px] text-emerald-300" data-testid={`recent-booked-${h.id}`}>
                        <span className="relative inline-flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                        </span>
                        <span className="uppercase tracking-[0.15em]">
                            <span className="font-semibold text-gold">{recent.masked_name}</span> {t("trust.recently_booked")}
                            {(() => {
                                if (!recent.when) return null;
                                try {
                                    const then = new Date(recent.when).getTime();
                                    const now = Date.now();
                                    const min = Math.max(1, Math.floor((now - then) / 60000));
                                    let str;
                                    if (min < 60) str = `${min} dk önce`;
                                    else if (min < 1440) str = `${Math.floor(min / 60)} saat önce`;
                                    else str = `${Math.floor(min / 1440)} gün önce`;
                                    return <span className="text-muted_green"> · {str}</span>;
                                } catch { return null; }
                            })()}
                        </span>
                    </div>
                )}

                {amenities.length > 0 && (
                    <div className="mt-4 flex items-center gap-x-4 gap-y-2 flex-wrap text-muted_green border-t border-border_green pt-4">
                        {amenities.map(({ icon: Icon, label }) => (
                            <div key={label} className="flex items-center gap-1.5 text-xs" title={label}>
                                <Icon className="w-4 h-4 text-gold" />
                                <span className="hidden lg:inline">{label}</span>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-auto pt-5 border-t border-border_green flex items-end justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green" data-testid={`price-date-hint-${h.id}`}>
                            {h.rooms?.length || 0} oda tipi · Gece başına{checkIn && checkOut ? ` · ${fmtDay(checkIn)}–${fmtDay(checkOut)}` : ""}
                        </div>
                        {strike > 0 && lowest && strikePct > 0 ? (
                            <div className="flex items-center gap-2 mt-1" data-testid={`discount-strike-${h.id}`}>
                                <span className="line-through text-muted_green text-sm">{formatPrice(strike)}</span>
                                <span className="text-white text-[10px] font-bold px-1.5 py-0.5 tracking-wider" style={{ background: "#c0392b" }}>−%{strikePct}</span>
                                {dateDrop && <span className="text-[10px] text-emerald-400 font-semibold">{en ? "price drop for these dates" : "bu tarihlerde fiyat düştü"}</span>}
                            </div>
                        ) : null}
                        <div className="flex items-baseline gap-2 mt-1 flex-wrap">
                            <div className="font-serif text-3xl lg:text-4xl text-gold leading-none">{lowest ? formatPrice(lowest) : "—"}</div>
                            <span className="text-[11px] text-muted_green italic" data-testid={`starting-from-${h.id}`}>{en ? "per night, starting from" : "gecelik, başlayan fiyatlarla"}</span>
                        </div>
                        {lowest > 0 && (
                            <div className="text-xs mt-1.5" data-testid={`total-stay-${h.id}`}>
                                <span className="text-muted_green">{nights} {en ? (nights > 1 ? "nights total" : "night total") : "gece toplam"}</span>{" "}
                                <span className="text-bone font-bold font-mono">{formatPrice(stayPrice)}</span>
                            </div>
                        )}
                        <div className="text-xs text-muted_green mt-1.5">
                            <span className="text-emerald-400 font-semibold">Ücretsiz iptal</span>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <Link to={`/hotels/${h.id}`} className="text-xs text-bone/70 hover:text-gold underline underline-offset-2" data-testid={`details-link-${h.id}`}>Detay & oda tipleri</Link>
                        <button onClick={() => onQuickBook(h.id)} className="btn-gold !py-3 !px-5 !text-sm" data-testid={`quick-book-${h.id}`}>
                            Müsaitliği Gör <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const Home = () => {
    const { t, formatPrice, settings, lang } = useApp();
    const navigate = useNavigate();
    const [hotels, setHotels] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [cityFilter, setCityFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState("popular");

    const today = todayISO();
    const tomorrow = addDaysISO(today, 1);
    const [search, setSearch] = useState({ check_in: today, check_out: tomorrow, adults: 2, children: 0, infants: 0, rooms: 1, breakdown: { double: 1, triple: 0, quad: 0 } });
    const searchGuests = search.adults + search.children;

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/hotels${cityFilter ? `?city=${cityFilter}` : ""}`);
                setHotels(data);
            } catch { /* list load failed; empty state shown */ }
            finally { setLoading(false); }
        };
        load();
    }, [cityFilter]);

    useEffect(() => {
        api.get("/transfers").then(({ data }) => setTransfers(data || [])).catch(() => {});
    }, []);

    // Search analytics: log date/guest searches (debounced, skip initial defaults)
    const searchLoggedRef = React.useRef(null);
    useEffect(() => {
        const key = `${search.check_in}|${search.check_out}|${searchGuests}`;
        if (searchLoggedRef.current === null) { searchLoggedRef.current = key; return; }
        if (searchLoggedRef.current === key) return;
        const timer = setTimeout(() => {
            searchLoggedRef.current = key;
            api.post("/analytics/search", {
                check_in: search.check_in, check_out: search.check_out,
                guests: searchGuests, city: cityFilter || null, source: "home",
            }).catch(() => {});
        }, 1200);
        return () => clearTimeout(timer);
    }, [search, searchGuests, cityFilter]);

    const [recentByHotel, setRecentByHotel] = useState({});
    useEffect(() => {
        api.get("/hotels/recent-bookings?hours=720")
            .then(({ data }) => {
                const map = {};
                (data || []).forEach((r) => { if (r.hotel_id) map[r.hotel_id] = r; });
                setRecentByHotel(map);
            })
            .catch(() => {});
    }, []);

    const counts = useMemo(() => {
        const mecca = hotels.filter((h) => h.city === "Mecca").length;
        const medina = hotels.filter((h) => h.city === "Medina").length;
        return { mecca, medina, total: hotels.length };
    }, [hotels]);

    // Date-aware pricing: cheapest room for the selected stay (range > monthly > base)
    const searchNights = Math.max(1, nightsBetween(search.check_in, search.check_out) || 1);
    const priceInfoFor = useCallback((h) => {
        let best = null;
        for (const r of h.rooms || []) {
            const total = stayTotal(r, search.check_in, searchNights, false);
            if (total > 0 && (!best || total < best.total)) {
                best = { total, base: Number(r.price_individual) || 0 };
            }
        }
        if (!best) return { nightly: 0, total: 0, baseNightly: 0, nights: searchNights };
        return { nightly: best.total / searchNights, total: best.total, baseNightly: best.base, nights: searchNights };
    }, [search.check_in, searchNights]);

    const sortedHotels = useMemo(() => {
        const arr = [...hotels];
        const getPrice = (h) => priceInfoFor(h).nightly;
        switch (sort) {
            case "price_asc": return arr.sort((a, b) => getPrice(a) - getPrice(b));
            case "price_desc": return arr.sort((a, b) => getPrice(b) - getPrice(a));
            case "rating": return arr.sort((a, b) => (b.review_average || 0) - (a.review_average || 0));
            case "distance": {
                const parseD = (h) => parseInt(String(h.distance_to_haram || "").replace(/\D/g, ""), 10) || 9999;
                return arr.sort((a, b) => parseD(a) - parseD(b));
            }
            default: return arr;
        }
    }, [hotels, sort, priceInfoFor]);

    const handleQuickBook = (hotelId) => {
        const bd = search.breakdown || { double: search.rooms, triple: 0, quad: 0 };
        const params = new URLSearchParams({
            check_in: search.check_in, check_out: search.check_out,
            guests: searchGuests, adults: search.adults, children: search.children, infants: search.infants, rooms: search.rooms,
            bd_double: bd.double || 0, bd_triple: bd.triple || 0, bd_quad: bd.quad || 0,
        });
        navigate(`/hotels/${hotelId}?${params.toString()}`);
    };

    const pickCity = (c) => {
        setCityFilter(c);
        setTimeout(() => { document.getElementById("hotels")?.scrollIntoView({ behavior: "smooth" }); }, 150);
    };

    return (
        <PageTransition>
            {/* HERO */}
            <section className="relative overflow-hidden bg-emerald_deep">
                <div className="absolute inset-0 bg-cover bg-center hidden md:block scale-110 hero-bg-photo" style={{ backgroundImage: `url(${HERO_BG})`, filter: "blur(14px) brightness(0.32) saturate(0.65)" }} />
                <div className="absolute inset-0 md:hidden bg-no-repeat scale-110 hero-bg-photo" style={{ backgroundImage: `url(${HERO_BG_MOBILE})`, backgroundSize: "cover", backgroundPosition: "center 35%", filter: "blur(12px) brightness(0.30) saturate(0.65)" }} />
                <div className="absolute inset-0 bg-emerald_deep/60 hero-bg-wash" />
                <div className="absolute inset-0 bg-gradient-to-b from-emerald_deep/30 via-emerald_deep/40 to-emerald_deep hero-bg-fade" />
                <div className="absolute inset-0 arabesque-pattern opacity-15" />
                <div className="absolute -top-32 -left-32 w-96 h-96 bg-gold/12 rounded-full blur-[140px] pointer-events-none" />
                <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-gold/10 rounded-full blur-[120px] pointer-events-none" />

                <div className="relative max-w-6xl mx-auto px-6 lg:px-10 pt-28 pb-12 lg:pt-32 lg:pb-16 text-center">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
                        <div className="inline-flex items-center gap-2 bg-gold/15 backdrop-blur border border-gold/50 px-3 py-1.5 mb-6 pill-on-hero mx-auto">
                            <BadgeCheck className="w-3.5 h-3.5 text-gold" />
                            <span className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">{lang === "en" ? "Verified Stays in Makkah & Madinah" : "Mekke & Medine'de Doğrulanmış Konaklamalar"}</span>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-balance hero-text-light max-w-4xl mx-auto text-center">
                            {lang === "en" ? "Two Sacred Cities · One Trusted Address" : "İki Mübarek Belde · Tek Güvenilir Adres"}
                        </h1>
                        <p className="mt-6 text-lg lg:text-xl hero-text-light-muted max-w-2xl mx-auto leading-relaxed text-center">
                            {lang === "en" ? "Reliable stays at on-site verified hotels in Makkah & Madinah." : "Mekke ve Medine'de yerinde denetlenmiş otellerde güvenilir konaklama."}
                        </p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mt-10 max-w-5xl mx-auto">
                        <div className="bg-[#fdfbf7] shadow-2xl border-y-2 border-gold text-left">
                            <div className="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-emerald_deep/10">
                                <div className="lg:col-span-6">
                                    <DateRangePicker
                                        variant="light"
                                        lang={lang}
                                        value={{ from: search.check_in, to: search.check_out }}
                                        onChange={({ from, to }) => setSearch((p) => ({ ...p, check_in: from || p.check_in, check_out: to || p.check_out }))}
                                        testIds={{
                                            cellIn: "quick-checkin-cell",
                                            cellOut: "quick-checkout-cell",
                                            checkIn: "quick-checkin",
                                            checkOut: "quick-checkout",
                                        }}
                                    />
                                </div>
                                <div className="lg:col-span-2">
                                    <GuestSelector
                                        variant="light"
                                        lang={lang}
                                        showRooms
                                        showBreakdown
                                        value={{ adults: search.adults, children: search.children, infants: search.infants, rooms: search.rooms, breakdown: search.breakdown }}
                                        onChange={(g) => setSearch((p) => ({ ...p, ...g }))}
                                        testId="quick-guests"
                                    />
                                </div>
                                <div
                                    className="lg:col-span-2 p-4 cursor-pointer hover:bg-emerald_deep/5 transition-colors"
                                    onClick={(e) => { const sel = e.currentTarget.querySelector("select"); if (sel) { try { sel.showPicker?.(); } catch { /* noop */ } sel.focus(); } }}
                                >
                                    <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-950/70 mb-1 font-bold flex items-center gap-1.5">
                                        <MapPin className="w-3.5 h-3.5 text-gold" /> {lang === "en" ? "City" : "Şehir"}
                                    </div>
                                    <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} onClick={(e) => e.stopPropagation()} className="bg-transparent text-emerald-950 text-base font-semibold w-full outline-none" data-testid="quick-city-select">
                                        <option value="">{lang === "en" ? "All Cities" : "Tüm Şehirler"}</option>
                                        <option value="Mecca">{lang === "en" ? "Makkah" : "Mekke"}</option>
                                        <option value="Medina">{lang === "en" ? "Madinah" : "Medine"}</option>
                                    </select>
                                </div>
                                <a
                                    href="#hotels"
                                    onClick={() => api.post("/analytics/search", { check_in: search.check_in, check_out: search.check_out, guests: searchGuests, city: cityFilter || null, source: "home_search" }).catch(() => {})}
                                    className="lg:col-span-2 flex items-center justify-center gap-2 px-5 py-4 text-white font-bold uppercase tracking-[0.12em] text-base hover:brightness-95 transition-all" style={{ background: "var(--c-gold)" }} data-testid="quick-search-btn">
                                    <Search className="w-5 h-5" /> {lang === "en" ? "Search" : "Ara"}
                                </a>
                            </div>
                        </div>

                        <div className="mt-6 flex flex-wrap justify-center gap-2 text-xs text-bone/85" data-testid="hero-trust-pills">
                            {[
                                { icon: Headphones, label: lang === "en" ? "24/7 Live Support" : "7/24 Canlı Destek" },
                                { icon: BadgeCheck, label: lang === "en" ? "On-site verified hotels" : "Yerinde denetlenmiş oteller" },
                                { icon: Tag, label: lang === "en" ? "Best Price Guarantee" : "En İyi Fiyat Garantisi" },
                            ].map(({ icon: Icon, label }) => (
                                <div key={label} className="pill-on-hero inline-flex items-center gap-1.5 border border-bone/20 px-3 py-1.5">
                                    <Icon className="w-3.5 h-3.5 text-gold" />
                                    <span>{label}</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Booking-style trust ribbon — sits directly below the hero */}
            <section className="bg-emerald_surface border-y border-gold/30" data-testid="trust-ribbon">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                        {[
                            { icon: ShieldCheck, ...t("trust.secure_booking") },
                            { icon: Award, ...t("trust.best_price") },
                            { icon: BadgeCheck, ...t("trust.verified") },
                            { icon: Headphones, ...t("trust.support") },
                        ].map(({ icon: Icon, title, desc }) => (
                            <div key={title} className="flex items-center gap-3">
                                <div className="flex-shrink-0 w-10 h-10 border border-gold/40 flex items-center justify-center bg-emerald_deep/40">
                                    <Icon className="w-4 h-4 text-gold" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[11px] uppercase tracking-[0.15em] text-gold font-semibold leading-snug">{title}</div>
                                    <div className="text-[11px] text-muted_green leading-snug">{desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <PrayerTimes />

            {/* DESTINATIONS */}
            <section className="py-16 lg:py-20 max-w-6xl mx-auto px-6 lg:px-10">
                <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-10">
                    <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Hizmet Verdiğimiz Şehirler</div>
                    <h2 className="font-serif text-4xl lg:text-5xl text-balance">Hangi Beldede Konaklayacaksınız?</h2>
                </motion.div>
                <div className="grid md:grid-cols-2 gap-5">
                    {[
                        { city: "Mecca", label: "Mekke-i Mükerreme", landmark: "Mescid-i Haram", img: IMG_MECCA, count: counts.mecca, blurb: "Harem-i Şerif'e yürüme mesafesinde 5 yıldızlı konaklamalar" },
                        { city: "Medina", label: "Medine-i Münevvere", landmark: "Mescid-i Nebevi", img: IMG_MEDINA, count: counts.medina, blurb: "Hz. Peygamber'in (s.a.v.) mescidine en yakın seçkin oteller" },
                    ].map((c, i) => (
                        <motion.button
                            key={c.city}
                            onClick={() => pickCity(c.city)}
                            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.55, delay: i * 0.1 }}
                            whileHover={{ y: -4 }}
                            className="group relative overflow-hidden text-left h-72 md:h-80 border border-border_green hover:border-gold transition-colors"
                            data-testid={`city-card-${c.city}`}
                        >
                            <img src={c.img} alt={c.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                            <div className="absolute inset-0 bg-gradient-to-t from-emerald_deep via-emerald_deep/65 to-emerald_deep/15 image-overlay-dark" />
                            <div className="relative h-full flex flex-col justify-end p-7 lg:p-9">
                                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-gold mb-2 self-start bg-emerald_deep/70 px-2 py-1 backdrop-blur">
                                    <MapPin className="w-3 h-3" /> {c.landmark}
                                </div>
                                <div className="font-serif text-4xl lg:text-5xl leading-tight" style={{ color: "#fdfbf7" }}>{c.label}</div>
                                <p className="text-base mt-2 max-w-md" style={{ color: "rgba(253,251,247,0.88)" }}>{c.blurb}</p>
                                <div className="mt-4 flex items-center justify-between border-t pt-3" style={{ borderColor: "rgba(253,251,247,0.25)" }}>
                                    <span className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "#fdfbf7" }}>{c.count} Doğrulanmış Otel</span>
                                    <span className="inline-flex items-center gap-1.5 text-gold text-sm font-semibold uppercase tracking-[0.18em] group-hover:gap-2 transition-all">
                                        Otelleri Gör <ArrowRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        </motion.button>
                    ))}
                </div>
                <div className="mt-6 text-center">
                    <Link to="/map" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-gold hover:underline font-semibold" data-testid="open-map-link">
                        <MapIcon className="w-4 h-4" /> Otellerimizi Haritada Görüntüle
                    </Link>
                </div>
            </section>

            {/* WHY ASIS */}
            <section className="bg-emerald_surface border-y border-border_green">
                <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                        { icon: BadgeCheck, title: "Yerinde Denetlenmiş", desc: "Her otelin temizlik ve hizmet kalitesi ekibimizce doğrulanır." },
                        { icon: Headphones, title: "7/24 Türkçe Destek", desc: "WhatsApp üzerinden bize her an ulaşın." },
                        { icon: ShieldCheck, title: "Güvenli Ödeme", desc: "Online veya banka havalesi ile güvenli ödeme seçenekleri." },
                        { icon: Award, title: "En İyi Fiyat Garantisi", desc: "Daha ucuz fiyatı sizin için eşliyoruz." },
                    ].map(({ icon: Icon, title, desc }, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.4 }} className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-gold/15 border border-gold/40 flex-shrink-0 flex items-center justify-center">
                                <Icon className="w-4 h-4 text-gold" />
                            </div>
                            <div>
                                <div className="font-serif text-lg mb-1">{title}</div>
                                <div className="text-xs text-muted_green leading-relaxed">{desc}</div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* PROMO VIDEO */}
            {settings.promo_video_url && (
                <section className="py-16 lg:py-20 max-w-5xl mx-auto px-6 lg:px-10">
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }} className="text-center mb-8">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Tanıtım</div>
                        <h2 className="font-serif text-3xl lg:text-4xl text-balance">Şirketimizi Tanıyın</h2>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <video src={resolveImageUrl(settings.promo_video_url)} controls playsInline preload="metadata" className="w-full h-auto" data-testid="promo-video" />
                    </motion.div>
                </section>
            )}

            {/* HOTELS */}
            <section id="hotels" className="py-16 lg:py-20 max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="flex items-end justify-between flex-wrap gap-6 mb-8">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">
                            {cityFilter === "Mecca" ? "Mekke" : cityFilter === "Medina" ? "Medine" : "Tüm Oteller"}
                        </div>
                        <h2 className="font-serif text-4xl lg:text-5xl text-balance">
                            {loading ? "Oteller Yükleniyor…" : `${sortedHotels.length} otel bulundu`}
                        </h2>
                        <p className="text-sm text-muted_green mt-2">
                            {new Date(search.check_in).toLocaleDateString("tr-TR")} → {new Date(search.check_out).toLocaleDateString("tr-TR")} · {searchGuests} misafir{search.infants > 0 ? ` + ${search.infants} bebek` : ""}
                        </p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        {cityFilter && (
                            <button onClick={() => setCityFilter("")} className="text-[11px] uppercase tracking-[0.18em] text-gold hover:underline" data-testid="clear-city-filter">✕ Filtreyi temizle</button>
                        )}
                        <Link to="/map" className="text-[11px] uppercase tracking-[0.18em] text-gold hover:underline flex items-center gap-1.5 font-semibold" data-testid="see-on-map-link">
                            <MapIcon className="w-3.5 h-3.5" /> Haritada Gör
                        </Link>
                        <div className="flex items-center gap-2">
                            <label className="text-[11px] uppercase tracking-[0.18em] text-muted_green font-semibold">Sırala:</label>
                            <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-emerald_surface border border-border_green text-bone text-xs px-3 py-2 focus:border-gold outline-none" data-testid="sort-select">
                                {SORT_OPTIONS.map((o) => (<option key={o.id} value={o.id} className="bg-emerald_surface">{o.label}</option>))}
                            </select>
                        </div>
                    </div>
                </motion.div>

                {loading ? (
                    <div className="space-y-5">{[1, 2, 3].map((i) => (<div key={i} className="border border-border_green animate-pulse h-60 bg-emerald_surface" />))}</div>
                ) : sortedHotels.length === 0 ? (
                    <div className="border border-dashed border-border_green p-16 text-center" data-testid="no-hotels">
                        <BedDouble className="w-12 h-12 text-gold/60 mx-auto mb-3" />
                        <div className="font-serif text-2xl mb-1">Otel bulunamadı</div>
                        <div className="text-sm text-muted_green">Lütfen başka bir şehir veya tarih deneyin.</div>
                    </div>
                ) : (
                    <div className="space-y-5">
                        {sortedHotels.map((h) => (<HotelCard key={h.id} h={h} formatPrice={formatPrice} onQuickBook={handleQuickBook} recent={recentByHotel[h.id]} t={t} priceInfo={priceInfoFor(h)} checkIn={search.check_in} checkOut={search.check_out} />))}
                    </div>
                )}
            </section>

            {transfers.length > 0 && (
                <section id="transfers" className="py-16 lg:py-20 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-transfers-section">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                        <div>
                            <div className="text-xs uppercase tracking-[0.35em] text-gold mb-2">{lang === "en" ? "TRANSFER & TRANSPORT" : "TRANSFER & ULAŞIM"}</div>
                            <h2 className="font-serif text-3xl sm:text-4xl">{t("home.transfer_title")}</h2>
                            <p className="text-muted_green mt-2 max-w-xl text-sm leading-relaxed">{t("home.transfer_subtitle")}</p>
                        </div>
                        <Link to="/transfers" className="text-xs uppercase tracking-[0.18em] text-gold hover:underline inline-flex items-center gap-1" data-testid="home-transfers-all">
                            {t("home.transfer_see_all")} <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {transfers.slice(0, 3).map((tr) => {
                            const img = tr.image || (tr.images || [])[0];
                            return (
                                <Link key={tr.id} to="/transfers" className="group border border-border_green bg-emerald_surface overflow-hidden block" data-testid={`home-transfer-${tr.id}`}>
                                    <div className="aspect-[16/10] bg-emerald_deep relative overflow-hidden">
                                        {img ? (
                                            <img src={resolveImageUrl(img)} alt={tr.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-muted_green"><BusIcon className="w-12 h-12" /></div>
                                        )}
                                        <div className="absolute top-3 left-3 bg-emerald_deep/85 backdrop-blur px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-gold border border-gold/40">{tr.vehicle_type || "Otobüs"}</div>
                                    </div>
                                    <div className="p-4">
                                        <div className="font-serif text-lg text-bone truncate">{tr.name}</div>
                                        {tr.route && <div className="text-xs text-muted_green mt-1 truncate">{tr.route}</div>}
                                        {Number(tr.price_per_person) > 0 && (
                                            <div className="mt-2 text-xs"><span className="text-muted_green">Kişi başı:</span> <span className="text-gold font-bold font-mono">{formatPrice(tr.price_per_person)}</span></div>
                                        )}
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            )}

            <section id="guides-banner" className="py-16 lg:py-20 bg-emerald_surface border-y border-border_green" data-testid="home-guides-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="text-xs uppercase tracking-[0.35em] text-gold mb-3">{t("home.guides_kicker")}</div>
                        <h2 className="font-serif text-3xl sm:text-4xl leading-tight">{t("home.guides_title_line1")}<br />{t("home.guides_title_line2")}</h2>
                        <p className="text-bone/80 mt-4 leading-relaxed">{t("home.guides_desc")}</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                            {[
                                { icon: ShieldCheck, title: t("home.guide_feature_1_title"), desc: t("home.guide_feature_1_desc") },
                                { icon: Users, title: t("home.guide_feature_2_title"), desc: t("home.guide_feature_2_desc") },
                                { icon: BadgeCheck, title: t("home.guide_feature_3_title"), desc: t("home.guide_feature_3_desc") },
                                { icon: Headphones, title: t("home.guide_feature_4_title"), desc: t("home.guide_feature_4_desc") },
                            ].map(({ icon: Icon, title, desc }) => (
                                <div key={title} className="flex items-start gap-3">
                                    <Icon className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                                    <div>
                                        <div className="text-sm font-semibold text-bone">{title}</div>
                                        <div className="text-xs text-muted_green">{desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Link to="/guides" className="btn-gold mt-8 inline-flex" data-testid="home-guides-cta">
                            {t("home.guides_cta")} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="relative">
                        <div className="aspect-[4/3] bg-emerald_deep border border-border_green flex items-center justify-center overflow-hidden">
                            <GraduationCap className="w-32 h-32 text-gold/40" />
                        </div>
                        <div className="absolute -bottom-4 -left-4 bg-emerald_deep border border-gold/40 p-4 max-w-xs shadow-xl">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-gold mb-1">{t("home.guides_quote_kicker")}</div>
                            <div className="text-sm text-bone leading-relaxed">
                                &ldquo;{t("home.guides_quote")}&rdquo;
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <SocialStrip settings={settings} />

            <FAQSection items={settings.faqs} />

            <section className="py-24 max-w-4xl mx-auto px-6 lg:px-10 text-center">
                <div className="ornament-divider mb-6"><span className="font-arabic text-3xl text-gold">۞</span></div>
                <h2 className="font-serif text-4xl lg:text-5xl text-balance">{t("sections.cta_title")}</h2>
                <p className="mt-5 text-base text-muted_green max-w-xl mx-auto">{t("sections.cta_subtitle")}</p>
                <div className="mt-9 flex items-center justify-center gap-4 flex-wrap">
                    <a href="#hotels" className="btn-gold" data-testid="cta-explore-btn"><span>Otelleri Gör</span> <ArrowRight className="w-4 h-4" /></a>
                    <Link to="/track" className="btn-outline-gold" data-testid="cta-track-btn"><ShieldCheck className="w-4 h-4" /> Rezervasyon Sorgula</Link>
                </div>
            </section>
        </PageTransition>
    );
};

export default Home;

